import {
  CAD_PROJECT_PIN_KIND_COLORS,
  CAD_PROJECT_PIN_IMAGE_PREFIX,
} from '../../Map/styles/mapTheme';
import {
  CAD_PROJECT_PIN_KIND_CUSTOM,
  cadProjectPinKindLabel,
  resolveCadProjectPinKind,
} from '../data/cadProjectPinKinds';
import {
  projectCadPlanPoint,
  readCadPlanBounds,
} from './cadGeometry';

const MAX_NAME = 120;
const MAX_NOTES = 500;
const MAX_KIND = 40;

/** Id local del pin en borrador (no persiste hasta Guardar). */
export const CAD_PROJECT_PIN_DRAFT_ID = '__cad-pin-draft__';

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function readFiniteNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Pin de preview mientras se coloca / edita. Nombre vacío → "Nuevo pin".
 *
 * @param {object|null|undefined} draft
 * @param {{ editingId?: string|null }} [opts]
 * @returns {object|null}
 */
export function buildCadProjectPinDraft(draft, { editingId = null } = {}) {
  if (!draft || typeof draft !== 'object') return null;
  const lat = readFiniteNumber(draft.lat);
  const lng = readFiniteNumber(draft.lng);
  const localX = readFiniteNumber(draft.local_x);
  const localY = readFiniteNumber(draft.local_y);
  const hasMap = lat != null && lng != null;
  const hasLocal = localX != null && localY != null;
  if (!hasMap && !hasLocal) return null;
  const id = String(editingId || '').trim() || CAD_PROJECT_PIN_DRAFT_ID;
  const name = String(draft.name || '').trim() || 'Nuevo pin';
  return normalizeCadProjectPin({
    id,
    name,
    kind: draft.kind,
    notes: draft.notes,
    lat: hasMap ? lat : null,
    lng: hasMap ? lng : null,
    local_x: hasLocal ? localX : null,
    local_y: hasLocal ? localY : null,
  });
}

/**
 * Pinta el borrador encima (o en lugar del pin que se está moviendo).
 *
 * @param {object|null|undefined} project
 * @param {object|null|undefined} draftPin
 * @returns {object|null|undefined}
 */
export function mergeCadProjectPinsWithDraft(project, draftPin) {
  if (!project || !draftPin) return project;
  const pins = readCadProjectPins(project).filter((pin) => (
    pin.id !== CAD_PROJECT_PIN_DRAFT_ID && pin.id !== draftPin.id
  ));
  return { ...project, pins: [...pins, draftPin] };
}

/**
 * @param {object|null|undefined} raw
 * @returns {object|null}
 */
export function normalizeCadProjectPin(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim();
  const name = String(raw.name || '').trim();
  if (!id || !name) return null;
  const kindRaw = String(raw.kind || '').trim().slice(0, MAX_KIND);
  const kind = kindRaw || null;
  const notesRaw = raw.notes == null ? null : String(raw.notes).trim();
  const notes = notesRaw || null;
  const lat = readFiniteNumber(raw.lat);
  const lng = readFiniteNumber(raw.lng);
  const localX = readFiniteNumber(raw.local_x);
  const localY = readFiniteNumber(raw.local_y);
  if (lat == null && lng == null && localX == null && localY == null) return null;
  return {
    id,
    project_id: String(raw.project_id || '').trim() || null,
    name: name.slice(0, MAX_NAME),
    kind,
    notes: notes ? notes.slice(0, MAX_NOTES) : null,
    lat,
    lng,
    local_x: localX,
    local_y: localY,
  };
}

/**
 * @param {unknown} pins
 * @returns {object[]}
 */
export function normalizeCadProjectPins(pins) {
  if (!Array.isArray(pins)) return [];
  return pins.map(normalizeCadProjectPin).filter(Boolean);
}

/**
 * @param {object|null|undefined} project
 * @returns {object[]}
 */
export function readCadProjectPins(project) {
  return normalizeCadProjectPins(project?.pins);
}

/**
 * Color del badge para un `kind` (fallback = custom).
 * @param {string|null|undefined} kind
 * @returns {string}
 */
export function getCadProjectPinKindColor(kind) {
  const resolved = resolveCadProjectPinKind(kind);
  return CAD_PROJECT_PIN_KIND_COLORS[resolved]
    || CAD_PROJECT_PIN_KIND_COLORS[CAD_PROJECT_PIN_KIND_CUSTOM]
    || CAD_PROJECT_PIN_KIND_COLORS.default;
}

/**
 * @param {string|null|undefined} kind
 * @returns {string}
 */
export function cadProjectPinImageId(kind) {
  return `${CAD_PROJECT_PIN_IMAGE_PREFIX}${resolveCadProjectPinKind(kind)}`;
}

/**
 * Coords para pintar según lienzo. Mapa → lat/lng; canvas/image → local →
 * lng/lat sintético del plano.
 *
 * @param {object} pin
 * @param {'map'|'plan'} paintMode
 * @param {object|null|undefined} project
 * @param {object|null|undefined} [featureCollection]
 * @returns {[number, number]|null} [lng, lat]
 */
export function cadProjectPinPaintCoordinates(pin, paintMode, project, featureCollection = null) {
  if (!pin) return null;
  if (paintMode === 'plan') {
    const x = readFiniteNumber(pin.local_x);
    const y = readFiniteNumber(pin.local_y);
    if (x == null || y == null) return null;
    const bounds = readCadPlanBounds(project, featureCollection);
    return projectCadPlanPoint(x, y, bounds);
  }
  const lat = readFiniteNumber(pin.lat);
  const lng = readFiniteNumber(pin.lng);
  if (lat == null || lng == null) return null;
  return [lng, lat];
}

/**
 * @param {object[]} pins
 * @param {'map'|'plan'} paintMode
 * @param {object|null|undefined} project
 * @param {object|null|undefined} [featureCollection]
 * @returns {{ type: string, features: object[] }}
 */
export function cadProjectPinsToGeoJSON(pins, paintMode, project, featureCollection = null) {
  const features = (Array.isArray(pins) ? pins : [])
    .map((pin) => {
      const coords = cadProjectPinPaintCoordinates(pin, paintMode, project, featureCollection);
      if (!coords) return null;
      const kind = resolveCadProjectPinKind(pin.kind);
      return {
        type: 'Feature',
        id: pin.id,
        geometry: { type: 'Point', coordinates: coords },
        properties: {
          id: pin.id,
          name: pin.name || '',
          notes: pin.notes || '',
          kind,
          kindLabel: cadProjectPinKindLabel(kind),
          color: getCadProjectPinKindColor(kind),
          icon: cadProjectPinImageId(kind),
        },
      };
    })
    .filter(Boolean);
  return { type: 'FeatureCollection', features };
}

/**
 * Tooltip: nombre + notes, o nombre + kind si no hay notes.
 * `variant: 'share'` → chrome de lotes en `/#/lista/:id`.
 * Default = POI (`terrio-place-tooltip`).
 *
 * @param {object} props
 * @param {{ variant?: 'place'|'share' }} [options]
 * @returns {string}
 */
export function buildCadProjectPinTooltipHtml(props, { variant = 'place' } = {}) {
  const name = String(props?.name || '').trim();
  const notes = String(props?.notes || '').trim();
  const kindLabel = String(props?.kindLabel || cadProjectPinKindLabel(props?.kind) || '').trim();
  if (!name && !notes && !kindLabel) return '';
  const escape = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Con descripción: título + descripción. Sin descripción: título + kind.
  const title = name || (notes ? kindLabel : (kindLabel || ''));
  const subtitle = notes || (!notes && name ? kindLabel : '');

  if (variant === 'share') {
    const rows = [];
    if (title) {
      rows.push(`<div class="terrio-cad-share-tooltip__title">${escape(title)}</div>`);
    }
    if (subtitle && subtitle !== title) {
      rows.push(`<div class="terrio-cad-share-tooltip__muted">${escape(subtitle)}</div>`);
    }
    return rows.length
      ? `<div class="terrio-cad-share-tooltip__card">${rows.join('')}</div>`
      : '';
  }

  const parts = [];
  if (title) {
    parts.push(`<div class="terrio-place-tooltip__title">${escape(title)}</div>`);
  }
  if (subtitle && subtitle !== title) {
    parts.push(`<div class="terrio-place-tooltip__name">${escape(subtitle)}</div>`);
  }
  return parts.join('');
}

/**
 * Upsert local en `project.pins` tras create/update.
 * @param {object|null|undefined} project
 * @param {object} pin
 * @returns {object|null|undefined}
 */
export function upsertCadProjectPinInProject(project, pin) {
  if (!project) return project;
  const nextPin = normalizeCadProjectPin(pin);
  if (!nextPin) return project;
  const prev = normalizeCadProjectPins(project.pins);
  const idx = prev.findIndex((row) => row.id === nextPin.id);
  const pins = idx >= 0
    ? prev.map((row, i) => (i === idx ? nextPin : row))
    : [...prev, nextPin];
  return { ...project, pins };
}

/**
 * @param {object|null|undefined} project
 * @param {string} pinId
 * @returns {object|null|undefined}
 */
export function removeCadProjectPinFromProject(project, pinId) {
  if (!project) return project;
  const id = String(pinId || '').trim();
  if (!id) return project;
  const pins = normalizeCadProjectPins(project.pins).filter((row) => row.id !== id);
  return { ...project, pins };
}

/**
 * Filtra pines del manage por nombre, tipo o notas.
 * @param {object[]} pins
 * @param {string} [query]
 * @returns {object[]}
 */
export function filterCadProjectPinsByQuery(pins, query = '') {
  const list = Array.isArray(pins) ? pins : [];
  const q = String(query || '').trim().toLowerCase();
  if (!q) return list;
  return list.filter((pin) => {
    const haystack = [
      pin?.name,
      pin?.notes,
      cadProjectPinKindLabel(pin?.kind),
      pin?.kind,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  });
}
